import React, { forwardRef, useMemo } from 'react';
import { Skeleton, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { FoldersTable } from '@components/Tables';
import { AssetItemDto } from '@api/Api';
import { reorganizeFolderList } from '@redux/actions/assetsAction';
import { DraggableList } from '@components/DraggableList/DraggableList';
import { DraggableItem } from '@components/DraggableList';
import useTypedDispatch from '@hooks/useTypedDispatch';
import FolderActions from '@components/Tables/FolderActions';
import { FolderIcon, FolderPrivateIcon } from '@icons';

function FoldersList({
  foldersList,
  countToFetch,
  isCardViewMode,
  canReorganize,
  canDropInside,
  listLoading,
  onAssetDrop,
  onFolderDrop,
  canShare
}: {
  foldersList: {
    node: AssetItemDto;
    cursor: string;
  }[];
  countToFetch: number;
  isCardViewMode: boolean;
  canReorganize?: boolean;
  canDropInside?: boolean;
  listLoading: boolean;
  onAssetDrop?: (assetId: string, folderId: string) => void;
  onFolderDrop?: (sourceFolderId: string, targetFolderId: string) => void;
  canShare?: boolean;
}) {
  const dispatch = useTypedDispatch();
  return (
    <div>
      {isCardViewMode ? (
        <div
          className="assets-container"
          style={{
            paddingRight: 34,
            paddingLeft: 34,
            marginRight: -34,
            marginLeft: -34
          }}
        >
          <DraggableList
            className="assets-container-list"
            gap={16}
            disableReorder={!canReorganize}
            onReorder={(e) => dispatch(reorganizeFolderList(e))}
            onDropInside={(e) => {
              if (e.item.type === 'asset' && onAssetDrop) {
                onAssetDrop(e.item.id, e.insideItem.id);
              }
              if (e.item.type === 'folder' && onFolderDrop) {
                onFolderDrop(e.item.id, e.insideItem.id);
              }
            }}
          >
            {foldersList
              .concat(new Array(countToFetch).fill(''))
              .map(({ node }, index) => {
                if (!node)
                  return (
                    <div key={index} className="assets_folder_container">
                      <Skeleton.Input style={{ width: '100%', height: 56 }} />
                    </div>
                  );

                return (
                  <DraggableItem
                    key={node.id}
                    id={node.id}
                    type="folder"
                    droppable={canDropInside ? ['asset', 'folder'] : false}
                  >
                    <FolderEntity node={node} canShare={canShare} />
                  </DraggableItem>
                );
              })}
          </DraggableList>
        </div>
      ) : (
        <div className="assets-container-list">
          <FoldersTable
            foldersList={foldersList}
            onDrop={onAssetDrop}
            onFolderDrop={onFolderDrop}
            onReorder={(e) => dispatch(reorganizeFolderList(e))}
            canReorganize={canReorganize}
            countToFetch={countToFetch}
            listLoading={listLoading}
            canShare={canShare}
          />
        </div>
      )}
    </div>
  );
}

const FolderEntity = forwardRef(
  (
    {
      node,
      canShare
    }: {
      node: AssetItemDto;
      canShare?: boolean;
    },
    ref: any
  ) => {
    const navigate = useNavigate();
    const assetsCountCopy = useMemo(() => {
      const count = node.folder?.totalAssetsCount || 0;
      return `${count} media`;
    }, [node.folder?.totalAssetsCount]);

    return (
      <div ref={ref} className="assets_folder_container">
        <div
          className="assets_folder"
          onClick={() => {
            if (node.deletedAt) return;
            navigate(`/campaigns/single/${node.campaignId}/${node.id}`);
          }}
          style={{
            cursor: node.deletedAt ? 'default' : undefined
          }}
        >
          <div className="assets_folder__body">
            {node?.isPrivate ? <FolderPrivateIcon /> : <FolderIcon />}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Tooltip
                placement="topLeft"
                overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
                title={node?.folder?.name}
              >
                <span>{node?.folder?.name}</span>
              </Tooltip>

              <span className="assets-count">{assetsCountCopy}</span>
            </div>
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <FolderActions node={node} canShare={canShare} />
          </div>
        </div>
      </div>
    );
  }
);

export default FoldersList;
